import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Loader2, CheckCircle2, AlertTriangle, ExternalLink } from 'lucide-react';

// Popup opened by the Runn bookmarklet. Fetches the caller's timesheet values
// for one person + one week, then postMessages the payload back to the opener
// tab (Runn) and closes itself.
//
// Query params:
//   runn_person_id  — required. Numeric string from Runn's /people/<id> URL.
//   week_start      — required. Monday of the target week, YYYY-MM-DD.
//   origin          — optional. The exact origin of the opener tab we may
//                     post to. Defaults to https://app.runn.io. Only origins
//                     on the allowlist below are honored — a page opened
//                     from anywhere else can't trick us into leaking data
//                     to a different site.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ALLOWED_OPENER_ORIGINS = new Set([
  'https://app.runn.io',
]);

const RunnFillPopup = () => {
  const [status, setStatus] = useState('loading'); // loading | done | error | not-authed
  const [errorMsg, setErrorMsg] = useState('');
  const [summary, setSummary] = useState(null);

  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const runnPersonId = params.get('runn_person_id') || '';
  const weekStart = params.get('week_start') || '';
  const openerOriginRaw = params.get('origin') || 'https://app.runn.io';
  const openerOrigin = ALLOWED_OPENER_ORIGINS.has(openerOriginRaw) ? openerOriginRaw : null;

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!runnPersonId || !weekStart) {
        setErrorMsg('Missing runn_person_id or week_start in the URL. Try the bookmarklet again from Runn.');
        setStatus('error');
        return;
      }
      if (!openerOrigin) {
        setErrorMsg(`This popup can only post back to allowed Runn origins. Rejected: ${openerOriginRaw}`);
        setStatus('error');
        return;
      }
      if (!window.opener) {
        setErrorMsg('This page must be opened from the Runn tab by the Fill from Harmony bookmarklet.');
        setStatus('error');
        return;
      }

      try {
        // Popup is opened from Runn's origin so sessionStorage is empty here.
        // The user's HttpOnly harmony_refresh cookie IS sent because the
        // popup is on the harmony origin — trade it for a fresh access token
        // and use that as the Bearer.
        let bearer = '';
        try {
          const rr = await axios.post(
            `${BACKEND_URL}/api/auth/refresh`,
            {},
            { withCredentials: true },
          );
          bearer = rr?.data?.token || '';
        } catch (refreshErr) {
          if (refreshErr?.response?.status === 401) {
            setStatus('not-authed');
            return;
          }
          throw refreshErr;
        }
        if (!bearer) {
          setStatus('not-authed');
          return;
        }
        const r = await axios.get(`${API}/runn/fill-values`, {
          params: { runn_person_id: runnPersonId, week_start: weekStart },
          headers: { Authorization: `Bearer ${bearer}` },
        });
        if (cancelled) return;
        const data = r.data || {};
        window.opener.postMessage(
          { type: 'harmony-runn-fill', payload: data },
          openerOrigin,
        );
        setSummary({
          person: data.person_name,
          projectCount: (data.projects || []).length,
          unmappedCount: (data.unmapped_projects || []).length,
          weekStart: data.week_start,
        });
        setStatus('done');
        // Auto-close after a short delay so HR sees the confirmation.
        setTimeout(() => {
          try { window.close(); } catch (_) { /* noop */ }
        }, 1200);
      } catch (err) {
        if (cancelled) return;
        const s = err?.response?.status;
        if (s === 401 || s === 403) {
          setStatus('not-authed');
          return;
        }
        if (s === 404) {
          setErrorMsg(err?.response?.data?.detail || 'This person isn\'t mapped in Harmony yet.');
          setStatus('error');
          return;
        }
        setErrorMsg(err?.response?.data?.detail || err?.message || 'Failed to load values.');
        setStatus('error');
      }
    })();

    return () => { cancelled = true; };
  }, [runnPersonId, weekStart, openerOrigin, openerOriginRaw]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md border border-slate-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <img src="/favicon.ico" alt="" className="w-5 h-5" />
          <h1 className="text-lg font-semibold text-slate-800">Fill from Harmony</h1>
        </div>

        {status === 'loading' && (
          <div className="flex items-center gap-3 text-slate-600">
            <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
            <div className="text-sm">
              Fetching timesheet values for<br />
              <span className="font-mono text-xs text-slate-500">
                Runn #{runnPersonId} · week of {weekStart}
              </span>
            </div>
          </div>
        )}

        {status === 'done' && (
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
            <div className="text-sm text-slate-700 space-y-1">
              <p className="font-medium">Sent to Runn.</p>
              {summary && (
                <p className="text-xs text-slate-500">
                  {summary.person || 'Person'}, week of {summary.weekStart} ·{' '}
                  {summary.projectCount} project{summary.projectCount === 1 ? '' : 's'}
                  {summary.unmappedCount > 0 && ` · ${summary.unmappedCount} unmapped`}
                </p>
              )}
              <p className="text-xs text-slate-400">Closing…</p>
            </div>
          </div>
        )}

        {status === 'not-authed' && (
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div className="text-sm text-slate-700">
                <p className="font-medium mb-1">You need to sign in to Harmony first.</p>
                <p className="text-xs text-slate-500">
                  Open Harmony in a new tab, sign in, then come back to Runn and click the bookmarklet again.
                </p>
              </div>
            </div>
            <a
              href="/login"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
            >
              Open Harmony sign-in <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        )}

        {status === 'error' && (
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-rose-600 mt-0.5" />
            <div className="text-sm text-slate-700 space-y-2">
              <p className="font-medium">Couldn't fetch values.</p>
              <p className="text-xs text-slate-500 break-words">{errorMsg}</p>
              <button
                type="button"
                onClick={() => window.close()}
                className="text-xs text-blue-600 hover:underline"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RunnFillPopup;
